import { Box, Group, NumberInput, Text, UnstyledButton } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { Dot } from "../../delivery/atoms"
import { CategoryTreeFilter, type CategorySelection } from "../category-tree-filter"
import classes from "./catalog.module.css"
import { STOCK_TONE } from "./StockLabel"
import type { FilterState } from "./types"
import type { StockLevel } from "./utils"

type Props = {
  filters: FilterState
  onFiltersChange: (updater: (f: FilterState) => FilterState) => void
  /** Products with every filter but the category applied: the source of the category counts. */
  categoryProducts: any[]
  /** Products with every filter but the stock level applied: the source of the stock counts. */
  stockCounts: Record<StockLevel, number>
  categories: CategorySelection[]
  onCategoriesChange: (selection: CategorySelection[]) => void
  /** Cheapest / dearest price in the whole catalogue: the price inputs' placeholders. */
  priceBounds: { min: number; max: number } | null
}

const STOCK_OPTIONS: { level: StockLevel; key: string }[] = [
  { level: "ready", key: "catalog.stockReadyLegend" },
  { level: "low", key: "catalog.stockLowLegend" },
  { level: "out", key: "catalog.stockOutLegend" },
]

/** Left rail (canvas OrdB_Catalog): category tree (multi-select) with counts, price range, stock level. Sort lives in the toolbar only (B15). */
export function FilterSidebar({ filters, onFiltersChange, categoryProducts, stockCounts, categories, onCategoriesChange, priceBounds }: Props) {
  const { t } = useTranslation("order")

  const setPrice = (field: "priceMin" | "priceMax", value: string | number) => {
    const n = typeof value === "number" ? value : null
    onFiltersChange((f) => (f[field] === n ? f : { ...f, [field]: n }))
  }

  const toggleStock = (level: StockLevel) =>
    onFiltersChange((f) => ({
      ...f,
      stockLevels: f.stockLevels.includes(level) ? f.stockLevels.filter((l) => l !== level) : [...f.stockLevels, level],
    }))

  return (
    <Box className={classes.sidebar}>
      <CategoryTreeFilter
        products={categoryProducts}
        total={categoryProducts.length}
        selected={categories}
        onChange={onCategoriesChange}
      />

      <Box>
        <Text className={classes.sectionTitle} mb="xs">
          {t("catalog.priceTitle")}
        </Text>
        <Group gap="xs" wrap="nowrap" align="flex-end">
          <NumberInput
            label={t("catalog.priceMin")}
            placeholder={priceBounds ? priceBounds.min.toLocaleString("en-US") : undefined}
            key={`min-${filters.priceMin ?? ""}`}
            defaultValue={filters.priceMin ?? ""}
            min={0}
            thousandSeparator=","
            hideControls
            onBlur={(e) => setPrice("priceMin", e.currentTarget.value === "" ? "" : Number(e.currentTarget.value.replace(/,/g, "")))}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur()
            }}
          />
          <NumberInput
            label={t("catalog.priceMax")}
            placeholder={priceBounds ? priceBounds.max.toLocaleString("en-US") : undefined}
            key={`max-${filters.priceMax ?? ""}`}
            defaultValue={filters.priceMax ?? ""}
            min={0}
            thousandSeparator=","
            hideControls
            onBlur={(e) => setPrice("priceMax", e.currentTarget.value === "" ? "" : Number(e.currentTarget.value.replace(/,/g, "")))}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur()
            }}
          />
        </Group>
      </Box>

      <Box>
        <Text className={classes.sectionTitle} mb="xs">
          {t("catalog.stockTitle")}
        </Text>
        {STOCK_OPTIONS.filter(({ level }) => level !== "out" || stockCounts.out > 0 || filters.stockLevels.includes("out")).map(({ level, key }) => (
          <UnstyledButton
            key={level}
            className={classes.stockOption}
            data-selected={filters.stockLevels.includes(level) || undefined}
            aria-pressed={filters.stockLevels.includes(level)}
            onClick={() => toggleStock(level)}
          >
            <Dot tone={STOCK_TONE[level]} />
            <Text span inherit>
              {t(key)}
            </Text>
            <Text span className={classes.navCount}>
              {stockCounts[level]}
            </Text>
          </UnstyledButton>
        ))}
        <Text className={classes.meta} mt="sm">
          {t("catalog.facetNote")}
        </Text>
      </Box>
    </Box>
  )
}
